import { z } from 'zod';
import type { RelayChatExtension, RunStore } from '@inference.engine/agentic-react';

const debugPartSchema = z.object({
  event: z.string(),
  agentId: z.string().optional(),
  at: z.string(),
  detail: z.record(z.string(), z.unknown()),
});

export const DEFINITION_DEBUG_EXTENSION = 'definitionDebug';
/** What each canvas node a tool call came from did last, by canvas node id. */
export const DEFINITION_CANVAS_EXTENSION = 'definitionCanvas';

export interface CanvasNodeRun {
  /** The agent invocation it ran in, which ties it to one run. */
  runNodeId: string;
  status: 'running' | 'completed' | 'error';
  startedAt: number;
  durationMs?: number;
  /** The calls still running on it: a server or a knowledge base can serve several at once. */
  pending: string[];
  failed: boolean;
}

export type CanvasNodeRuns = Record<string, CanvasNodeRun>;

/** What ran, by definition id: a published version number, or `'draft'`. */
export interface DefinitionRunVersions {
  versions: Record<string, number | 'draft'>;
  rootDraftRevision: number | null;
}

/**
 * Reads the API's admin-only `data-definitionDebug` parts: which canvas node each tool call came
 * from, so the canvas lights it, and the versions a run resolved, so a panel can say what ran. The
 * steps themselves come from the run's trace (`traceExtension`), which carries their payloads.
 */
export const definitionDebugExtension: RelayChatExtension = {
  part: 'data-definitionDebug',
  onPart: (data, { store }) => {
    const parsed = debugPartSchema.safeParse(data);
    if (!parsed.success) return;
    applyDebugEvent(store, parsed.data);
  },
};

const canvasRefSchema = z.object({
  nodeIds: z.array(z.string()).min(1),
  runNodeId: z.string(),
  callId: z.string(),
});

type ToolEvent = 'tool-start' | 'tool-end' | 'tool-error';
const TOOL_EVENTS: ReadonlySet<string> = new Set<ToolEvent>([
  'tool-start',
  'tool-end',
  'tool-error',
]);

function nextNodeRun(
  prior: CanvasNodeRun | undefined,
  event: ToolEvent,
  call: { runNodeId: string; callId: string; at: number },
): CanvasNodeRun {
  const live = prior?.runNodeId === call.runNodeId && prior.pending.length > 0 ? prior : undefined;
  if (event === 'tool-start') {
    return live
      ? { ...live, pending: [...live.pending, call.callId] }
      : {
          runNodeId: call.runNodeId,
          status: 'running',
          startedAt: call.at,
          pending: [call.callId],
          failed: false,
        };
  }
  const pending = live?.pending.filter((id) => id !== call.callId) ?? [];
  const failed = event === 'tool-error' || live?.failed === true;
  const startedAt = live?.startedAt ?? call.at;
  const done = pending.length === 0;
  return {
    runNodeId: call.runNodeId,
    status: !done ? 'running' : failed ? 'error' : 'completed',
    startedAt,
    pending,
    failed,
    ...(done && live && !failed ? { durationMs: call.at - startedAt } : {}),
  };
}

/** Lights the canvas nodes a tool call names; a call it cannot place lights none. */
function applyCanvasEvent(store: RunStore, part: z.infer<typeof debugPartSchema>): void {
  const ref = canvasRefSchema.safeParse(part.detail);
  if (!TOOL_EVENTS.has(part.event) || !ref.success) return;
  const event = part.event as ToolEvent;
  const { nodeIds, runNodeId, callId } = ref.data;
  const call = { runNodeId, callId, at: Date.parse(part.at) };
  store.getState().updateExtension<CanvasNodeRuns>(DEFINITION_CANVAS_EXTENSION, (current) => {
    const next = { ...current };
    for (const nodeId of nodeIds) {
      next[nodeId] = nextNodeRun(next[nodeId] as CanvasNodeRun | undefined, event, call);
    }
    return next;
  });
}

function applyDebugEvent(store: RunStore, part: z.infer<typeof debugPartSchema>): void {
  const { event, detail } = part;
  applyCanvasEvent(store, part);
  if (event !== 'versions') return;
  const versions = z
    .record(z.string(), z.union([z.number(), z.literal('draft')]))
    .safeParse(detail.versions);
  if (!versions.success) return;
  const rootDraftRevision =
    typeof detail.rootDraftRevision === 'number' ? detail.rootDraftRevision : null;
  store
    .getState()
    .updateExtension<DefinitionRunVersions>(DEFINITION_DEBUG_EXTENSION, (current) => ({
      versions: { ...current?.versions, ...versions.data },
      rootDraftRevision: rootDraftRevision ?? current?.rootDraftRevision ?? null,
    }));
}
